<template>
    <DsPopover v-model:open="open">
        <DsPopoverTrigger v-bind="$attrs">
            <slot name="menuTrigger" />
        </DsPopoverTrigger>
        <DsPopoverContent
            :align
            :avoidCollisions
            :side
            :sideOffset
            :teleport
        >
            <ul
                class="ds-menu-content overflow-y-auto"
                v-bind="$attrs"
                :class="fullWidth ? 'w-[var(--reka-popover-trigger-width)]' : 'min-w-56'"
            >
                <DsMenuGroup v-if="!hasNestedGroup">
                    <slot />
                </DsMenuGroup>
                <template v-else>
                    <slot />
                </template>
            </ul>
        </DsPopoverContent>
    </DsPopover>
</template>

<script setup lang="ts">
import DsMenuGroup from '@/components/design-system/menu/DsMenuGroup.vue';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import { slotHasChildrenOfType } from '@/components/design-system/utils/slot-children-util';
import type { PopoverContentProps } from 'reka-ui';
import type { Slot } from 'vue';
import { computed, provide } from 'vue';

interface DsMenuProps extends Pick<PopoverContentProps, 'align' | 'avoidCollisions' | 'side'> {
    fullWidth?: boolean;
    sideOffset?: number;
    teleport?: boolean;
}

withDefaults(defineProps<DsMenuProps>(), {
    align: 'end',
    side: 'bottom',
    sideOffset: 4,
});

defineOptions({
    inheritAttrs: false,
});

const slots = defineSlots<{
    menuTrigger: Slot;
    default: Slot;
}>();
const hasNestedGroup = computed(() => slotHasChildrenOfType(slots.default, DsMenuGroup));

const open = defineModel<boolean>('open');

// provide the close function to the child options
provide('closeMenu', () => {
    open.value = false;
});
</script>

<style scoped lang="scss">
.ds-menu-content {
    :deep(> li) {
        @apply px-2;

        &.ds-menu-group {
            @apply py-2;
        }

        &.ds-menu-option:not(.ds-menu-option ~ .ds-menu-option) {
            @apply pt-2;
        }

        &.ds-menu-option:not(:has(~ .ds-menu-option)) {
            @apply pb-2;
        }
    }
}
</style>
